/**
 * Lineage Hooks
 *
 * React Query hooks for lineage data and operations
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { lineageApi } from '@/lib/api/lineage';
import type {
  LineageOptions,
  LineageHistoryFilters,
  TimeTravelRequest,
} from '@/features/lineage/types/lineage.types';

/**
 * Query keys for lineage
 */
export const lineageKeys = {
  all: ['lineage'] as const,
  graph: (entityId: string, options: LineageOptions) =>
    [...lineageKeys.all, 'graph', entityId, options] as const,
  search: (query: string) => [...lineageKeys.all, 'search', query] as const,
  dataset: (datasetId: string, direction: string, depth: number) =>
    [...lineageKeys.all, 'dataset', datasetId, direction, depth] as const,
};

/**
 * Get lineage graph for an entity
 */
export const useLineageGraph = (entityId: string | null, options: LineageOptions) => {
  return useQuery({
    queryKey: lineageKeys.graph(entityId || '', options),
    queryFn: () => lineageApi.getLineage(entityId!, options),
    enabled: !!entityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Search lineage entities
 */
export const useLineageSearch = (query: string) => {
  return useQuery({
    queryKey: lineageKeys.search(query),
    queryFn: () => lineageApi.searchLineageEntities(query),
    enabled: query.length > 2,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Get dataset-specific lineage
 */
export const useDatasetLineage = (
  datasetId: string | null,
  direction: 'upstream' | 'downstream' | 'both' = 'both',
  depth: number = 5
) => {
  return useQuery({
    queryKey: lineageKeys.dataset(datasetId || '', direction, depth),
    queryFn: () => lineageApi.getDatasetLineage(datasetId!, direction, depth),
    enabled: !!datasetId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ============================================================================
// Lineage History Hooks
// ============================================================================

/**
 * Query keys for lineage history
 */
export const lineageHistoryKeys = {
  all: ['lineage-history'] as const,
  snapshots: (filters?: LineageHistoryFilters) =>
    [...lineageHistoryKeys.all, 'snapshots', filters] as const,
  stats: () => [...lineageHistoryKeys.all, 'stats'] as const,
};

/**
 * Get list of lineage snapshots
 */
export const useSnapshots = (filters?: LineageHistoryFilters, enabled = true) => {
  return useQuery({
    queryKey: lineageHistoryKeys.snapshots(filters),
    queryFn: () => lineageApi.getSnapshots(filters),
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Get snapshot statistics
 */
export const useSnapshotStats = () => {
  return useQuery({
    queryKey: lineageHistoryKeys.stats(),
    queryFn: () => lineageApi.getSnapshotStats(),
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Time-travel mutation for on-demand requests
 */
export const useTimeTravelMutation = () => {
  return useMutation({
    mutationFn: (request: TimeTravelRequest) => lineageApi.timeTravel(request),
  });
};
